import { useEffect } from 'react';
import {
  useMatch,
  Outlet,
  useNavigate,
  NavLink,
  useParams,
  generatePath,
} from 'react-router-dom';
import {
  PermissionEnum,
  PermissionDenied,
  IconLoader,
  IconExpand,
  Tabs,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import { setOrganizationId } from '../store/slices/security';

import ROUTES from '../../../routes';

import cn from './styles.module.css';

const IndividualCustomerSecurity = (): React.JSX.Element => {
  const navigate = useNavigate();

  const match = useMatch(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY);
  const pageMatch = useMatch(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOCKS);
  const logsPageMatch = useMatch(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOGS);
  const devicesPageMatch = useMatch(
    ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_DEVICES,
  );

  const { customerId } = useParams();
  const dispatch = useAppDispatch();

  const { activePermissions } = useAppSelector((state) => state.root);

  useEffect(() => {
    if (match && activePermissions?.includes(PermissionEnum.LOCKS_LIST)) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOCKS, {
          customerId: customerId as string,
        }),
      );
    } else if (
      match &&
      !activePermissions?.includes(PermissionEnum.LOCKS_LIST) &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOGS, {
          customerId: customerId as string,
        }),
      );
    } else if (
      match &&
      !activePermissions?.includes(PermissionEnum.LOCKS_LIST) &&
      !activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) &&
      activePermissions?.includes(PermissionEnum.CUSTOMERS_DEVICES_LIST)
    ) {
      navigate(
        generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_DEVICES, {
          customerId: customerId as string,
        }),
      );
    }
  }, [match, activePermissions]);

  useEffect(() => {
    if (customerId) {
      dispatch(setOrganizationId(customerId));
    }
  }, [customerId]);

  let content = (
    <div className={cn.securityLoader}>
      <IconLoader />
    </div>
  );

  let createLink = '';

  if (pageMatch) {
    createLink = generatePath(
      ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOCKS_CREATE,
      {
        customerId: customerId as string,
      },
    );
  } else if (logsPageMatch) {
    createLink = generatePath(
      ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOGS_CREATE,
      {
        customerId: customerId as string,
      },
    );
  } else if (devicesPageMatch) {
    createLink = generatePath(
      ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_DEVICES_CREATE,
      {
        customerId: customerId as string,
      },
    );
  }

  const tabsVisible = Boolean(
    (activePermissions?.includes(PermissionEnum.LOCKS_LIST) &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) &&
      activePermissions?.includes(PermissionEnum.CUSTOMERS_DEVICES_LIST)) ||
    (activePermissions?.includes(PermissionEnum.LOCKS_LIST) &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)) ||
    (activePermissions?.includes(PermissionEnum.LOCKS_LIST) &&
      activePermissions?.includes(PermissionEnum.CUSTOMERS_DEVICES_LIST)) ||
    (activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) &&
      activePermissions?.includes(PermissionEnum.CUSTOMERS_DEVICES_LIST)),
  );

  if (activePermissions) {
    if (
      !activePermissions.includes(PermissionEnum.LOCKS_LIST) &&
      !activePermissions.includes(PermissionEnum.AUDIT_LOG_LIST) &&
      !activePermissions.includes(PermissionEnum.CUSTOMERS_DEVICES_LIST)
    ) {
      content = <PermissionDenied />;
    } else {
      content = (
        <div className={cn.securityContent}>
          <div className={cn.securityContentTop}>
            <div className={cn.securityContentTitle}>
              <div className={cn.securityContentTitleText}>Security</div>
              {activePermissions?.includes(
                PermissionEnum.LOCKS_CREATE_ACTION,
              ) && (
                <NavLink to={createLink} className={cn.securityCreateLink}>
                  <div className={cn.iconWrapper}>
                    <IconExpand />
                  </div>
                  <div className={cn.textWrapper}>Block a user</div>
                </NavLink>
              )}
            </div>

            <div className={cn.securityTabsWrapper}>
              {tabsVisible && (
                <Tabs
                  itemClassName={cn.securityTabItem}
                  wrapperClassName={cn.securityTabs}
                  tabs={[
                    {
                      name: 'Locks',
                      path: generatePath(
                        ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOCKS,
                        {
                          customerId: customerId as string,
                        },
                      ),
                      hidden: Boolean(
                        !activePermissions.includes(PermissionEnum.LOCKS_LIST),
                      ),
                    },
                    {
                      name: 'Audit Logs',
                      path: generatePath(
                        ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_LOGS,
                        {
                          customerId: customerId as string,
                        },
                      ),
                      hidden: Boolean(
                        !activePermissions.includes(
                          PermissionEnum.AUDIT_LOG_LIST,
                        ),
                      ),
                    },
                    {
                      name: 'Devices',
                      path: generatePath(
                        ROUTES.CUSTOMERS_INDIVIDUAL_SECURITY_DEVICES,
                        {
                          customerId: customerId as string,
                        },
                      ),
                      hidden: Boolean(
                        !activePermissions.includes(
                          PermissionEnum.CUSTOMERS_DEVICES_LIST,
                        ),
                      ),
                    },
                  ]}
                  enableRouting
                />
              )}
            </div>
          </div>
          <div className={cn.securityContentBottom}>
            <Outlet />
          </div>
        </div>
      );
    }
  }

  return <div className={cn.securityContentWrapper}>{content}</div>;
};

export default IndividualCustomerSecurity;
